import { useState, useEffect, useMemo } from 'react';
import { useDashboard } from './useDashboard';
import { SearchResult, Tile, Group } from '../types';

interface UseSearchOptions {
  debounceMs?: number;
  minQueryLength?: number;
  searchInGroups?: boolean;
}

interface UseSearchReturn {
  searchQuery: string;
  searchResults: SearchResult[];
  isSearching: boolean;
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;
  performSearch: (query: string) => Promise<void>;
  // Local search results (for offline/instant search)
  localSearchResults: SearchResult[];
}

export const useSearch = (options: UseSearchOptions = {}): UseSearchReturn => {
  const {
    debounceMs = 300,
    minQueryLength = 2,
    searchInGroups = false
  } = options;

  const {
    searchQuery,
    searchResults,
    setSearchQuery: setDashboardSearchQuery,
    performSearch: dashboardPerformSearch,
    clearSearch: dashboardClearSearch,
    config,
    activeTab
  } = useDashboard();

  const [isSearching, setIsSearching] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchQuery, debounceMs]);

  // Perform search when debounced query changes
  useEffect(() => {
    if (debouncedQuery && debouncedQuery.length >= minQueryLength) {
      setIsSearching(true);
      dashboardPerformSearch(debouncedQuery).finally(() => {
        setIsSearching(false);
      });
    } else if (debouncedQuery.length === 0) {
      dashboardClearSearch();
    }
  }, [debouncedQuery, minQueryLength, dashboardPerformSearch, dashboardClearSearch]);

  // Local search results (for instant feedback)
  const localSearchResults = useMemo((): SearchResult[] => {
    if (!activeTab || !searchQuery || searchQuery.length < minQueryLength) {
      return [];
    }

    const results: SearchResult[] = [];
    const query = searchQuery.toLowerCase();

    for (const group of activeTab.groups) {
      // Search in group names if enabled
      if (searchInGroups && group.name.toLowerCase().includes(query)) {
        // Add all visible tiles from matching group
        for (const tile of group.tiles.filter(t => t.isVisible)) {
          results.push({
            type: 'tile',
            item: tile,
            group: {
              id: group.id,
              name: group.name,
              icon: group.icon,
              color: group.color,
              order: group.order,
              isCollapsible: group.isCollapsible,
              isCollapsed: group.isCollapsed,
              tiles: []
            },
            matchType: 'title' // Group name match
          });
        }
      }

      // Search in tiles
      for (const tile of group.tiles) {
        if (!tile.isVisible) continue;

        const titleMatch = tile.title.toLowerCase().includes(query);
        const descriptionMatch = tile.description.toLowerCase().includes(query);
        const urlMatch = tile.url.toLowerCase().includes(query);

        if (titleMatch || descriptionMatch || urlMatch) {
          results.push({
            type: 'tile',
            item: tile,
            group: {
              id: group.id,
              name: group.name,
              icon: group.icon,
              color: group.color,
              order: group.order,
              isCollapsible: group.isCollapsible,
              isCollapsed: group.isCollapsed,
              tiles: []
            },
            matchType: titleMatch ? 'title' : (descriptionMatch ? 'description' : 'title')
          });
        }
      }
    }

    return results;
  }, [activeTab, searchQuery, minQueryLength, searchInGroups]);

  const setSearchQuery = (query: string) => {
    setDashboardSearchQuery(query);
  };

  const clearSearch = () => {
    dashboardClearSearch();
    setIsSearching(false);
  };

  const performSearch = async (query: string) => {
    setIsSearching(true);
    try {
      await dashboardPerformSearch(query);
    } finally {
      setIsSearching(false);
    }
  };

  return {
    searchQuery,
    searchResults,
    isSearching,
    setSearchQuery,
    clearSearch,
    performSearch,
    localSearchResults
  };
};